import { useState } from 'react';
import { supabase } from '../supabaseClient';
import StarField from '../components/StarField';
import { KRIPTO3_STATE, PODIA_COURSE_URL, SUPPORT_EMAIL } from '../config/podia';
import { TAG_HOWTO_URL } from '../config/links';

// Mirrors web3kurzus.hu/login (landing src/pages/LoginPage.jsx): same card,
// background, consent checkboxes, button and feedback texts. Two additions
// specific to this app: the headline speaks about the courses, and the
// collapsible "Korábban a Podián vásároltál?" block under the card.

const CYAN = '#00D2C8';
const CYAN_BRIGHT = '#00F5EA';
const LANDING = 'https://web3kurzus.hu';

const Checkbox = ({ checked, onToggle, children }) => (
  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
    <div
      onClick={onToggle}
      style={{
        flexShrink: 0, width: 16, height: 16, borderRadius: 4, marginTop: 1,
        border: `2px solid ${checked ? CYAN : 'rgba(255,255,255,0.2)'}`,
        background: checked ? CYAN : 'transparent',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'all .15s', cursor: 'pointer',
      }}
    >
      {checked && <span style={{ color: '#0a0f1e', fontSize: 10, fontWeight: 900, lineHeight: 1 }}>✓</span>}
    </div>
    <span style={{ fontSize: 13, color: '#9ca3af', lineHeight: 1.55, userSelect: 'none' }}>{children}</span>
  </label>
);

const PodiaHint = () => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ marginTop: 18, textAlign: 'center' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        className="lg-podia-btn"
      >
        {KRIPTO3_STATE === 'live' ? 'Podián vásároltál?' : 'Korábban a Podián vásároltál?'} {open ? '▴' : '▾'}
      </button>
      {open && (
        <div style={{
          marginTop: 12, textAlign: 'left',
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 12, padding: '14px 18px',
          color: 'rgba(255,255,255,0.5)', fontSize: 13, lineHeight: 1.6,
        }}>
          {KRIPTO3_STATE === 'live' ? (
            <>
              <p style={{ margin: '0 0 8px' }}>
                A Podián vásárolt <strong style={{ color: '#fff' }}>Kriptovaluta Tanfolyam 3.0</strong> ide költözött.
              </p>
              <p style={{ margin: '0 0 8px' }}>
                Lépj be <strong style={{ color: '#fff' }}>ugyanazzal az email-címmel</strong>, amellyel a Podián vásároltál
                — a hozzáférésed automatikusan aktiválódik, külön regisztráció nem kell.
              </p>
              <p style={{ margin: 0 }}>
                Más címmel vásároltál, vagy nem látod a tanfolyamot? Írj nekünk a{' '}
                <a href={`mailto:${SUPPORT_EMAIL}`} className="lg-consent-link">{SUPPORT_EMAIL}</a>{' '}
                címre, és add meg a Podián használt email-címedet is.
              </p>
            </>
          ) : KRIPTO3_STATE === 'partial' ? (
            <>
              <p style={{ margin: '0 0 8px' }}>
                Ugyanazzal az email-címmel belépve a <strong style={{ color: '#fff' }}>Kriptovaluta Tanfolyam 3.0</strong>{' '}
                videós leckéi már itt is elérhetők, automatikusan.
              </p>
              <p style={{ margin: '0 0 8px' }}>
                A teljes anyag egyelőre a Podián is megvan:{' '}
                {PODIA_COURSE_URL ? (
                  <a href={PODIA_COURSE_URL} target="_blank" rel="noreferrer" className="lg-consent-link">
                    kriptovalutatanfolyam.podia.com
                  </a>
                ) : 'a Podián'}.
              </p>
              <p style={{ margin: 0 }}>
                Ha más email-címmel vásároltál, írj a{' '}
                <a href={`mailto:${SUPPORT_EMAIL}`} className="lg-consent-link">{SUPPORT_EMAIL}</a>-ra.
              </p>
            </>
          ) : (
            <>
              <p style={{ margin: '0 0 8px' }}>
                A <strong style={{ color: '#fff' }}>Kriptovaluta 3.0</strong> tanfolyamot egyelőre{' '}
                {PODIA_COURSE_URL ? (
                  <>itt éred el:{' '}
                    <a href={PODIA_COURSE_URL} target="_blank" rel="noreferrer" className="lg-consent-link" style={{ wordBreak: 'break-all' }}>
                      {PODIA_COURSE_URL}
                    </a>.
                  </>
                ) : (
                  <>a Podián éred el.</>
                )}
              </p>
              <p style={{ margin: 0 }}>
                Hamarosan ide költözik, és ugyanazzal az email-címmel automatikusan hozzáférsz.
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [aszf, setAszf] = useState(false);
  const [newsletter, setNewsletter] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    setError('');
    if (!email.trim()) { setError('Add meg az email címed.'); return; }
    if (!aszf) { setError('Az ÁSZF elfogadása kötelező a regisztrációhoz.'); return; }
    setLoading(true);
    const { error: err } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
        data: {
          gdpr_consent: true,
          gdpr_consent_at: new Date().toISOString(),
          newsletter_consent: newsletter,
          // E-mailek 1. kör: a marketing-hozzájárulás a szöveg verziójával, mint a
          // landingen; az adatbázis (email_consent_on_signup) naplózza.
          ...(newsletter ? { marketing_consent: 'reg-v1-2026-11' } : {}),
        },
      },
    });
    setLoading(false);
    if (err) {
      setError('Hiba történt. Ellenőrizd az email címet és próbáld újra.');
    } else {
      setSent(true);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0a0f1e',
      backgroundImage: 'radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)',
      backgroundSize: '28px 28px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '32px 16px',
      fontFamily: "'Inter', system-ui, sans-serif",
      position: 'relative',
      boxSizing: 'border-box',
    }}>
      <style>{`
        .lg-input {
          width: 100%;
          box-sizing: border-box;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 10px;
          padding: 12px 14px;
          color: #fff;
          font-size: 14px;
          font-family: 'Inter', system-ui, sans-serif;
          outline: none;
          transition: border-color .2s, box-shadow .2s;
        }
        .lg-input:focus {
          border-color: ${CYAN};
          box-shadow: 0 0 0 3px rgba(0,210,200,0.12);
        }
        .lg-input::placeholder { color: rgba(255,255,255,0.2); }
        .lg-back-link {
          color: rgba(255,255,255,0.3);
          text-decoration: none;
          font-size: 13px;
          transition: color .2s;
        }
        .lg-back-link:hover { color: ${CYAN}; }
        .lg-resend-btn, .lg-podia-btn {
          font-size: 13px;
          color: rgba(255,255,255,0.35);
          background: none;
          border: none;
          cursor: pointer;
          font-family: inherit;
          transition: color .2s;
        }
        .lg-resend-btn { text-decoration: underline; }
        .lg-resend-btn:hover, .lg-podia-btn:hover { color: ${CYAN}; }
        .lg-howto-link {
          color: rgba(255,255,255,0.55);
          text-decoration: none;
          transition: color .2s;
        }
        .lg-howto-link:hover { color: ${CYAN}; }
        .lg-consent-link {
          color: ${CYAN};
          text-decoration: none;
        }
        .lg-consent-link:hover { text-decoration: underline; }
      `}</style>

      <StarField />

      {/* Ambient glow */}
      <div style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 600,
        maxWidth: '100vw',
        height: 400,
        background: 'radial-gradient(ellipse, rgba(0,210,200,0.10) 0%, rgba(140,80,255,0.06) 50%, transparent 70%)',
        filter: 'blur(50px)',
        pointerEvents: 'none',
        zIndex: 0,
      }} />

      <div style={{ width: '100%', maxWidth: 420, position: 'relative', zIndex: 1 }}>
        <div style={{
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 20,
          padding: '40px 36px',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          boxShadow: '0 8px 60px rgba(0,0,0,0.5)',
        }}>
          {sent ? (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 48, marginBottom: 20, lineHeight: 1 }}>📧</div>
              <h2 style={{ fontSize: 22, fontWeight: 700, color: '#fff', marginBottom: 12 }}>
                Nézd meg a postafiókod!
              </h2>
              <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.45)', lineHeight: 1.65, marginBottom: 8 }}>
                Elküldtük a belépési linket a következő címre:
              </p>
              <p style={{ fontSize: 15, fontWeight: 700, color: CYAN, marginBottom: 24, wordBreak: 'break-all' }}>
                {email}
              </p>
              <div style={{
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.07)',
                borderRadius: 12,
                padding: '14px 18px',
                marginBottom: 28,
                textAlign: 'left',
              }}>
                <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)', lineHeight: 1.65, margin: 0 }}>
                  Ha nem látod a levelet, ellenőrizd a{' '}
                  <strong style={{ color: 'rgba(255,255,255,0.55)' }}>spam / levélszemét</strong>{' '}
                  mappát is. A link 60 percig érvényes.
                </p>
              </div>
              <button
                className="lg-resend-btn"
                onClick={() => { setSent(false); setEmail(''); setAszf(false); setNewsletter(false); }}
              >
                Más emailt adok meg
              </button>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 28 }}>
                <img
                  src="/logo2-256.png"
                  alt="web3kurzus"
                  style={{ height: 112, width: 'auto', mixBlendMode: 'lighten' }}
                />
              </div>

              <div style={{ marginBottom: 24 }}>
                <p style={{ fontSize: 32, fontWeight: 800, color: '#fff', lineHeight: 1.2, margin: 0 }}>
                  Lépj be a kripto tanfolyamaidhoz —{' '}
                  <span style={{ color: CYAN }}>jelszó nélkül.</span>
                </p>
              </div>

              <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)', marginBottom: 20 }} />

              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 13, color: 'rgba(255,255,255,0.6)', marginBottom: 7, fontWeight: 500 }}>
                  Email cím
                </label>
                <input
                  className="lg-input"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                  placeholder="pelda@gmail.com"
                  autoFocus
                />
              </div>

              <div style={{ marginBottom: 12 }}>
                <Checkbox checked={aszf} onToggle={() => setAszf(v => !v)}>
                  Elolvastam és elfogadom az{' '}
                  <a href={`${LANDING}/aszf`} target="_blank" rel="noopener noreferrer" className="lg-consent-link">
                    ÁSZF-t
                  </a>{' '}
                  és az{' '}
                  <a href={`${LANDING}/adatkezelesi-tajekoztato`} target="_blank" rel="noopener noreferrer" className="lg-consent-link">
                    Adatkezelési Tájékoztatót
                  </a>. Tudomásul veszem hogy betöltöttem a 18. életévemet.
                </Checkbox>
              </div>

              <div style={{ marginBottom: 20 }}>
                <Checkbox checked={newsletter} onToggle={() => setNewsletter(v => !v)}>
                  Kérem a web3kurzus tippjeit és ajánlatait e-mailben. Bármikor leiratkozhatok.
                </Checkbox>
              </div>

              {error && (
                <div style={{ color: '#f87171', fontSize: 13, marginBottom: 16, padding: '10px 14px', background: 'rgba(248,113,113,0.08)', borderRadius: 10, border: '1px solid rgba(248,113,113,0.15)' }}>
                  {error}
                </div>
              )}

              <button
                onClick={handleSubmit}
                disabled={loading}
                onMouseEnter={e => { if (!loading) e.currentTarget.style.background = CYAN_BRIGHT; }}
                onMouseLeave={e => { if (!loading) e.currentTarget.style.background = CYAN; }}
                style={{
                  width: '100%',
                  border: 'none',
                  borderRadius: 12,
                  padding: '13px',
                  fontSize: 15,
                  fontWeight: 700,
                  cursor: loading ? 'default' : 'pointer',
                  fontFamily: 'inherit',
                  transition: 'background .2s, box-shadow .2s',
                  background: loading ? 'rgba(100,130,255,0.08)' : CYAN,
                  color: loading ? 'rgba(255,255,255,0.25)' : '#0a0f1e',
                  boxShadow: loading ? 'none' : '0 0 24px rgba(0,210,200,0.2)',
                }}
              >
                {loading ? 'Küldés...' : '⚡ Belépő link küldése →'}
              </button>

              <p style={{ textAlign: 'center', marginTop: 14, fontSize: 12, color: 'rgba(255,255,255,0.18)' }}>
                A belépő linket emailben küldjük, 1 óráig érvényes.
              </p>
            </>
          )}
        </div>

        <p style={{ textAlign: 'center', margin: '20px 0 0', fontSize: 13 }}>
          <a href={TAG_HOWTO_URL} target="_blank" rel="noopener noreferrer" className="lg-howto-link">
            Még nincs hozzáférésed? Nézd meg, hogyan kapod meg a Kripto Startot →
          </a>
        </p>

        <PodiaHint />

        <p style={{ textAlign: 'center', marginTop: 20 }}>
          <a href={LANDING} className="lg-back-link">
            ← Vissza a web3kurzus.hu-ra
          </a>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
